import React, { useEffect, useState } from 'react';
import { Check, PenLine, RotateCcw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { formatUserFullName } from '../utils/userDisplayName';
import { jestPodpisObrazkowy } from '../utils/podpis';
import Podpis from './Podpis';

const MIN_DLUGOSC = 2;
const MAX_DLUGOSC = 100;
const DOZWOLONE = /^[\p{L}\p{M}\s.'’-]*$/u;

/**
 * Podpis pisany: imię i nazwisko (domyślnie z profilu, do poprawienia) zatwierdzane
 * przyciskiem „Podpisz” i wyświetlane czcionką odręczną.
 *
 * Interfejs zgodny z poprzednim rysowanym podpisem:
 *  - onSignatureChange(tekst | null)
 *  - signature: aktualna wartość (tekst albo obraz data:image ze starszego wniosku)
 */
const SignaturePad = ({
  onSignatureChange,
  width = 400,
  height = 150,
  signature = null,
  variant = 'default',
  /** Czytelne imię i nazwisko podpisującego — pokazywane pod podpisem (opcjonalnie) */
  signerName = null
}) => {
  const { user } = useAuth();
  const domyslny = formatUserFullName(user);
  const [tekst, setTekst] = useState(() => (signature && !jestPodpisObrazkowy(signature) ? signature : domyslny));

  useEffect(() => {
    if (!tekst && domyslny) setTekst(domyslny);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domyslny]);

  const podpisano = !!signature;
  const czysty = tekst.trim().replace(/\s+/g, ' ');
  const poprawny = czysty.length >= MIN_DLUGOSC && czysty.length <= MAX_DLUGOSC && DOZWOLONE.test(czysty);

  const podpisz = () => {
    if (!poprawny) return;
    if (onSignatureChange) onSignatureChange(czysty);
  };

  const wyczysc = () => {
    if (jestPodpisObrazkowy(signature)) setTekst(domyslny);
    if (onSignatureChange) onSignatureChange(null);
  };

  const isCompact = variant === 'compact';
  const ramka = isCompact
    ? 'rounded-xl bg-white ring-1 ring-gray-200/80 shadow-sm'
    : 'border-2 border-gray-300 rounded-lg bg-white';
  const przyciskDrugorzedny = isCompact
    ? 'flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors'
    : 'flex items-center space-x-1 px-3 py-1 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded';

  return (
    <div className="signature-pad-container" style={{ maxWidth: width }}>
      <div className={`relative flex flex-col justify-center px-4 py-3 ${ramka}`} style={{ minHeight: Math.min(height, 130) }}>
        {podpisano ? (
          <div className="flex items-center justify-center text-center">
            <Podpis value={signature} alt="Podpis" size="lg" className="max-h-24 max-w-full object-contain" />
          </div>
        ) : (
          <>
            <label className="block text-[11px] font-medium text-gray-500 mb-1">Imię i nazwisko (podpis)</label>
            <input
              type="text"
              value={tekst}
              maxLength={MAX_DLUGOSC}
              onChange={(e) => setTekst(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); podpisz(); } }}
              placeholder="Jan Kowalski"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="mt-2 min-h-[2.5rem] flex items-center justify-center text-center">
              {czysty ? (
                <span className="font-podpis text-3xl leading-tight text-gray-400">{czysty}</span>
              ) : (
                <span className="text-gray-400 text-sm">Wpisz imię i nazwisko, aby podpisać</span>
              )}
            </div>
          </>
        )}
      </div>
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className={`text-xs flex items-center gap-1 ${podpisano ? 'text-emerald-700' : 'text-gray-500'}`}>
          {podpisano ? <><Check className="h-3.5 w-3.5" />Podpisano</> : <><PenLine className="h-3.5 w-3.5" />Niepodpisane</>}
        </span>
        {podpisano ? (
          <button type="button" onClick={wyczysc} className={przyciskDrugorzedny}>
            <RotateCcw className="h-4 w-4" />
            <span>Wyczyść</span>
          </button>
        ) : (
          <button
            type="button"
            onClick={podpisz}
            disabled={!poprawny}
            title={poprawny ? 'Złóż podpis' : 'Podaj imię i nazwisko (2–100 znaków, tylko litery)'}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <PenLine className="h-4 w-4" />
            Podpisz
          </button>
        )}
      </div>
      {signerName && podpisano && jestPodpisObrazkowy(signature) ? (
        <p className={`mt-2 text-center text-sm font-medium text-gray-900 ${isCompact ? 'max-w-[min(100%,28rem)] mx-auto leading-snug' : ''}`}>
          {signerName}
        </p>
      ) : null}
    </div>
  );
};

export default SignaturePad;
